import type { Lang } from "./index";

// Copy for the pantry, recipes, shopping, craving and menu-photo screens in
// all three shipped languages. Each language is written natively (grammar,
// idiom, tone) — not a literal translation. Plural forms use function fields
// that take the count.

type MealTypeKey = "breakfast" | "lunch" | "dinner" | "snack";

// Stable keys for pantry/shopping categories. The DB stores these Polish
// strings as-is (legacy stable keys); only the displayed label is localized.
export type CategoryKey =
  | "nabiał"
  | "mięso"
  | "warzywa"
  | "owoce"
  | "pieczywo"
  | "suche"
  | "napoje"
  | "mrożone"
  | "słodycze"
  | "przyprawy"
  | "inne";

// Stable keys for pantry/shopping unit abbreviations. The DB stores these
// Polish abbreviations as-is; only the displayed label is localized.
export type UnitKey = "g" | "kg" | "ml" | "l" | "szt" | "opak" | "łyż" | "szkl";

export type PantryCopy = {
  // --- shared ---
  loading: string;
  cancel: string;
  save: string;
  saving: string;
  remove: string;
  errLoad: string;
  errDelete: string;
  errSave: string;
  errUpdate: string;
  errAddToList: string;
  errLog: string;

  // --- pantry-screen ---
  pantryMeta: string;
  pantrySuggestions: string;
  pantryTitle: string;
  pantryCountProducts: (n: number) => string;
  pantryExpiringNote: (name: string, today: boolean) => string;
  pantryCtaPhoto: string;
  pantryCtaManual: string;
  pantryShoppingLink: string;
  pantryEmpty: string;
  pantryStapleTag: string;
  pantryExpired: string;
  pantryToday: string;
  pantryTomorrow: string;
  pantryExpiryDays: (days: number) => string;
  pantryEditAria: (name: string) => string;
  pantryDeleteAria: (name: string) => string;
  pantrySharedNote: string;

  // --- pantry-add-screen ---
  addBack: string;
  addTopTitle: string;
  addEyebrow: string;
  addTitle: string;
  addIntro: string;
  addFieldWhat: string;
  addPlaceholderWhat: string;
  addFieldQuantity: string;
  addFieldUnit: string;
  addFieldCategory: string;
  addStapleStrong: string;
  addStapleRest: string;
  addSubmit: string;

  // --- pantry-photo-screen ---
  photoBack: string;
  photoTopTitle: string;
  photoTitle: string;
  photoIntro: string;
  photoNoItems: string;
  photoErrAnalyse: string;
  photoStatusAnalysing: string;
  photoStatusRec: string;
  photoViewfinderHint: string;
  photoTipLight: string;
  photoTipFromAbove: string;
  photoTipWholeFront: string;
  photoPreviewAlt: string;
  photoAriaManual: string;
  photoAriaRetake: string;
  photoAriaShoot: string;
  photoAriaGallery: string;
  photoCaptionAnalysing: string;
  photoCaptionRetake: string;
  photoCaptionShoot: string;

  // --- pantry-photo-confirm-screen ---
  confirmBack: string;
  confirmTopTitle: string;
  confirmCount: (n: number) => string;
  confirmDetected: string;
  confirmCheckHint: string;
  confirmEmpty: string;
  confirmStapleTag: string;
  confirmQtyUnknown: string;
  confirmAiFooter: string;
  confirmCta: (n: number) => string;

  // --- pantry-edit-modal ---
  editTitle: string;
  editFieldName: string;
  editFieldQuantity: string;
  editFieldUnit: string;
  editFieldCategory: string;
  editFieldExpiry: string;
  editStaple: string;

  // --- recipes-screen ---
  recipesTopTitle: string;
  recipesBack: string;
  recipesTitle: string;
  recipesCount: (n: number) => string;
  recipesBudgetNote: string;
  recipesFilterAll: string;
  recipesErrGenerate: string;
  recipesLoading: string;
  recipesEmpty: string;
  recipesHasAll: string;
  recipesMissing: (n: number) => string;
  recipesHaveLabel: string;
  recipesMissingLabel: string;
  recipesAddedToList: string;
  recipesAddingToList: string;
  recipesAddToList: string;
  recipesHowTo: string;
  recipesLogIt: string;
  recipesEdit: string;
  recipesOther: string;

  // --- shopping-list-screen ---
  shoppingBack: string;
  shoppingTopTitle: string;
  shoppingTitle: string;
  shoppingClear: string;
  shoppingErrClear: string;
  shoppingCountToBuy: (n: number) => string;
  shoppingDoneNote: (n: number) => string;
  shoppingEmpty: string;
  shoppingSectionToBuy: string;
  shoppingSectionDone: string;
  shoppingSourceManual: string;
  shoppingSourceRecipe: string;
  shoppingSourcePantryEmpty: string;
  shoppingCheckAria: (name: string) => string;
  shoppingUncheckAria: (name: string) => string;
  shoppingDeleteAria: (name: string) => string;
  shoppingCta: string;

  // --- shopping-add-screen ---
  shopAddBack: string;
  shopAddTopTitle: string;
  shopAddEyebrow: string;
  shopAddTitle: string;
  shopAddIntro: string;
  shopAddFieldWhat: string;
  shopAddPlaceholderWhat: string;
  shopAddFieldQuantity: string;
  shopAddFieldUnit: string;
  shopAddFieldCategory: string;
  shopAddSubmit: string;

  // --- craving-screen ---
  cravingBack: string;
  cravingTopTitle: string;
  cravingTitle: string;
  cravingPlaceholder: string;
  cravingBudgetPrefix: string;
  cravingBudgetEmpty: string;
  cravingPantryToggle: string;
  cravingSubmit: string;
  cravingThinking: string;
  cravingErrGenerate: string;
  cravingEmpty: string;
  cravingBestMatch: string;
  cravingAlternative: string;
  cravingStatKcal: string;
  cravingStatProtein: string;
  cravingStatFat: string;
  cravingStatCarbs: string;
  cravingConfHigh: string;
  cravingConfMid: string;
  cravingConfLow: string;
  cravingHaveLabel: string;
  cravingMissingLabel: string;
  cravingAddedToList: string;
  cravingAddingToList: string;
  cravingAddToList: string;
  cravingStepByStep: string;
  cravingLogIt: string;
  cravingEdit: string;

  // --- menu-photo-screen ---
  menuBack: string;
  menuTopTitle: string;
  menuOtherPhoto: string;
  menuPickTitle: string;
  menuPickIntro: string;
  menuPickPlaceholder: string;
  menuPreviewAlt: (n: number) => string;
  menuReading: string;
  menuPickCta: string;
  menuErrNoMenu: string;
  menuResultTitle: string;
  menuDishCount: (n: number) => string;
  menuRemainingPrefix: string;
  menuRemaining: (kcal: number) => string;
  menuFilterTop3: string;
  menuFilterAll: string;
  menuShowOriginal: (language: string) => string;
  menuShowTranslated: string;
  menuTopBadge: (n: number) => string;
  menuCollapse: string;
  menuExpand: string;
  menuLogIt: string;
  menuEdit: string;

  // --- meal type labels (display only) ---
  mealType: Record<MealTypeKey, string>;

  // --- pantry/shopping category labels (display only; DB keeps stable key) ---
  categoryLabel: Record<CategoryKey, string>;

  // --- pantry/shopping unit labels (display only; DB keeps stable key) ---
  unitLabel: Record<UnitKey, string>;
};

const pl: PantryCopy = {
  loading: "Ładuję...",
  cancel: "Anuluj",
  save: "Zapisz",
  saving: "Zapisuję...",
  remove: "Usuń",
  errLoad: "Nie udało się załadować",
  errDelete: "Nie udało się usunąć",
  errSave: "Coś nie wyszło",
  errUpdate: "Nie udało się zaktualizować",
  errAddToList: "Nie udało się dodać do listy",
  errLog: "Nie udało się zalogować",

  pantryMeta: "LODÓWKA",
  pantrySuggestions: "Sugestie →",
  pantryTitle: "Lodówka",
  pantryCountProducts: (n) => `${n} produktów`,
  pantryExpiringNote: (name, today) => `${name} psuje się ${today ? "dziś" : "jutro"} 👀`,
  pantryCtaPhoto: "ZDJĘCIE LODÓWKI",
  pantryCtaManual: "WPISZ",
  pantryShoppingLink: "Lista zakupów",
  pantryEmpty: "Pusto, jak po niedzieli u babci. Zacznij od zdjęcia lub wpisz produkt.",
  pantryStapleTag: "STAŁE",
  pantryExpired: "PRZETERMINOWANE",
  pantryToday: "DZIŚ",
  pantryTomorrow: "JUTRO",
  pantryExpiryDays: (days) => `${days}D`,
  pantryEditAria: (name) => `edytuj ${name}`,
  pantryDeleteAria: (name) => `usuń ${name}`,
  pantrySharedNote:
    "Lodówka jest wspólna w household. Wpis żony widzisz Ty, Twój wpis widzi rodzina.",

  addBack: "Lodówka",
  addTopTitle: "DODAJ PRODUKT",
  addEyebrow: "NOWY WPIS",
  addTitle: "Dodaj produkt",
  addIntro: "Wpis trafia do wspólnej lodówki. Reszta rodziny od razu zobaczy.",
  addFieldWhat: "Co",
  addPlaceholderWhat: "np. Mleko Łaciate 1L",
  addFieldQuantity: "Ilość",
  addFieldUnit: "Jednostka",
  addFieldCategory: "Kategoria",
  addStapleStrong: "Stałe zaopatrzenie.",
  addStapleRest: " Zawsze w domu, lista zakupów sama go przypomni.",
  addSubmit: "Wrzuć do lodówki →",

  photoBack: "Lodówka",
  photoTopTitle: "ZDJĘCIE LODÓWKI",
  photoTitle: "Lodówka? Pstryk",
  photoIntro: "Otwórz drzwi szeroko, w dobrym świetle. Etykiety pomagają.",
  photoNoItems:
    "Nie znalazłem produktów na zdjęciu. Otwórz lodówkę szerzej albo wpisz ręcznie.",
  photoErrAnalyse: "Nie udało się przeanalizować",
  photoStatusAnalysing: "● ANALIZA · SYTO SENSE",
  photoStatusRec: "● REC · SYTO SENSE",
  photoViewfinderHint: "ETYKIETY PRODUKTÓW DAJĄ NAM WIĘCEJ DO PRACY",
  photoTipLight: "💡 Dobre światło",
  photoTipFromAbove: "📐 Z góry",
  photoTipWholeFront: "🥑 Cały front",
  photoPreviewAlt: "podgląd lodówki",
  photoAriaManual: "Wpisz ręcznie",
  photoAriaRetake: "Inne zdjęcie",
  photoAriaShoot: "Zrób zdjęcie",
  photoAriaGallery: "Z galerii",
  photoCaptionAnalysing: "ZAGLĄDAMY DO LODÓWKI...",
  photoCaptionRetake: "STUKNIJ APARAT, ŻEBY POWTÓRZYĆ",
  photoCaptionShoot: "STUKNIJ APARAT, ŻEBY PSTRYKNĄĆ",

  confirmBack: "Wróć",
  confirmTopTitle: "POTWIERDŹ LODÓWKĘ",
  confirmCount: (n) => (n === 1 ? "1 rzecz" : `${n} rzeczy`),
  confirmDetected: "Wykryte produkty",
  confirmCheckHint: " · sprawdź ilości zanim zapiszesz.",
  confirmEmpty: "Wszystko usunięte. Wróć i pstryknij jeszcze raz.",
  confirmStapleTag: "STAŁE",
  confirmQtyUnknown: "ILOŚĆ ?",
  confirmAiFooter: "Wartości szacunkowe. Sprawdź przed zapisem.",
  confirmCta: (n) =>
    n === 1 ? "WRZUĆ 1 RZECZ DO LODÓWKI" : `WRZUĆ ${n} RZECZY DO LODÓWKI`,

  editTitle: "Popraw produkt",
  editFieldName: "Nazwa",
  editFieldQuantity: "Ilość",
  editFieldUnit: "Jednostka",
  editFieldCategory: "Kategoria",
  editFieldExpiry: "Termin (opcjonalnie)",
  editStaple: "Stałe zaopatrzenie",

  recipesTopTitle: "SUGESTIE",
  recipesBack: "Lodówka",
  recipesTitle: "Z tego co masz",
  recipesCount: (n) => `${n} ${n === 1 ? "propozycja" : "propozycje"}`,
  recipesBudgetNote: " · pod Twój budżet",
  recipesFilterAll: "Wszystko",
  recipesErrGenerate: "Nie udało się wygenerować",
  recipesLoading: "Zaglądamy do lodówki…",
  recipesEmpty:
    "Nic sensownego nie wymyślam z tego co masz. Dodaj coś do lodówki albo zmień filtr.",
  recipesHasAll: "MASZ WSZYSTKO",
  recipesMissing: (n) => `BRAK ${n}`,
  recipesHaveLabel: "MASZ",
  recipesMissingLabel: "BRAK",
  recipesAddedToList: "✓ na liście",
  recipesAddingToList: "dodaję…",
  recipesAddToList: "+ do listy",
  recipesHowTo: "Jak zrobić",
  recipesLogIt: "Zalogowałem to",
  recipesEdit: "Popraw",
  recipesOther: "Inne propozycje",

  shoppingBack: "Lodówka",
  shoppingTopTitle: "LISTA ZAKUPÓW",
  shoppingTitle: "Zakupy",
  shoppingClear: "Wyrzuć ✓",
  shoppingErrClear: "Nie udało się wyczyścić",
  shoppingCountToBuy: (n) => `${n} do kupienia`,
  shoppingDoneNote: (n) => ` · ${n} odhaczone`,
  shoppingEmpty: "Lista pusta. Dodaj rzecz albo wrzuć z sugestii przepisów.",
  shoppingSectionToBuy: "DO KUPIENIA",
  shoppingSectionDone: "ODHACZONE",
  shoppingSourceManual: "ręcznie",
  shoppingSourceRecipe: "z przepisu",
  shoppingSourcePantryEmpty: "uzupełnienie",
  shoppingCheckAria: (name) => `odhacz ${name}`,
  shoppingUncheckAria: (name) => `odznacz ${name}`,
  shoppingDeleteAria: (name) => `usuń ${name}`,
  shoppingCta: "DODAJ RZECZ",

  shopAddBack: "Lista",
  shopAddTopTitle: "DODAJ DO LISTY",
  shopAddEyebrow: "NOWA POZYCJA",
  shopAddTitle: "Dodaj do listy",
  shopAddIntro: "Co się przyda? Ilość i kategoria opcjonalne, sama nazwa wystarczy.",
  shopAddFieldWhat: "Co kupić",
  shopAddPlaceholderWhat: "np. masło, chleb, jabłka",
  shopAddFieldQuantity: "Ile (opcjonalnie)",
  shopAddFieldUnit: "Jednostka",
  shopAddFieldCategory: "Kategoria (opcjonalnie)",
  shopAddSubmit: "Dodaj do listy →",

  cravingBack: "Dziennik",
  cravingTopTitle: "POMYSŁ",
  cravingTitle: "Mam ochotę na",
  cravingPlaceholder: 'np. „coś sytego, mało węgli"',
  cravingBudgetPrefix: "BUDŻET: ",
  cravingBudgetEmpty: "BUDŻET: —",
  cravingPantryToggle: "Tylko z tego, co mam w lodówce",
  cravingSubmit: "Wymyśl przepis",
  cravingThinking: "Myślę…",
  cravingErrGenerate: "Nie udało się wymyślić",
  cravingEmpty: 'Nic sensownego nie wymyślam. Spróbuj inną ochotę albo wyłącz „tylko z lodówki".',
  cravingBestMatch: "NAJLEPSZE DOPASOWANIE",
  cravingAlternative: "ALTERNATYWA",
  cravingStatKcal: "kcal",
  cravingStatProtein: "białko",
  cravingStatFat: "tłuszcz",
  cravingStatCarbs: "węgle",
  cravingConfHigh: "pewne",
  cravingConfMid: "sprawdź",
  cravingConfLow: "niepewne",
  cravingHaveLabel: "MASZ",
  cravingMissingLabel: "BRAK",
  cravingAddedToList: "✓ na liście",
  cravingAddingToList: "dodaję…",
  cravingAddToList: "+ do listy",
  cravingStepByStep: "Krok po kroku",
  cravingLogIt: "Zaloguj to",
  cravingEdit: "Popraw",

  menuBack: "Dziennik",
  menuTopTitle: "MENU",
  menuOtherPhoto: "Inne zdjęcie",
  menuPickTitle: "Co zamawiamy",
  menuPickIntro:
    "Pstryknij menu w restauracji — możesz dodać do 3 zdjęć, jeśli karta ma kilka stron. Powiemy, ile kalorii ma każda pozycja i co najbardziej pasuje pod Twój dzień.",
  menuPickPlaceholder: "Wybierz zdjęcia menu (do 3)",
  menuPreviewAlt: (n) => `podgląd menu ${n}`,
  menuReading: "Czytamy menu…",
  menuPickCta: "Zrób / wybierz zdjęcia",
  menuErrNoMenu:
    "Nie znaleźliśmy menu na zdjęciu. Zrób bliższe zdjęcie albo bardziej proste menu z czytelnymi nazwami.",
  menuResultTitle: "Top dla Ciebie",
  menuDishCount: (n) => `${n} ${n === 1 ? "danie" : "dań"}`,
  menuRemainingPrefix: " · zostało ",
  menuRemaining: (kcal) => `${kcal} kcal`,
  menuFilterTop3: "★ Top 3 dla mnie",
  menuFilterAll: "Wszystkie",
  menuShowOriginal: (language) => `Pokaż ${language}`,
  menuShowTranslated: "Pokaż PL",
  menuTopBadge: (n) => `TOP ${n}`,
  menuCollapse: "ZWIŃ ↑",
  menuExpand: "ZAMAWIAM →",
  menuLogIt: "Zamówiłem to",
  menuEdit: "Popraw",

  mealType: {
    breakfast: "śniadanie",
    lunch: "obiad",
    dinner: "kolacja",
    snack: "przekąska",
  },

  categoryLabel: {
    nabiał: "nabiał",
    mięso: "mięso",
    warzywa: "warzywa",
    owoce: "owoce",
    pieczywo: "pieczywo",
    suche: "suche",
    napoje: "napoje",
    mrożone: "mrożone",
    słodycze: "słodycze",
    przyprawy: "przyprawy",
    inne: "inne",
  },

  unitLabel: {
    g: "g",
    kg: "kg",
    ml: "ml",
    l: "l",
    szt: "szt",
    opak: "opak",
    łyż: "łyż",
    szkl: "szkl",
  },
};

const en: PantryCopy = {
  loading: "Loading...",
  cancel: "Cancel",
  save: "Save",
  saving: "Saving...",
  remove: "Remove",
  errLoad: "Couldn't load",
  errDelete: "Couldn't delete",
  errSave: "Something went wrong",
  errUpdate: "Couldn't update",
  errAddToList: "Couldn't add to the list",
  errLog: "Couldn't log it",

  pantryMeta: "FRIDGE",
  pantrySuggestions: "Suggestions →",
  pantryTitle: "Fridge",
  pantryCountProducts: (n) => (n === 1 ? "1 product" : `${n} products`),
  pantryExpiringNote: (name, today) =>
    `${name} goes off ${today ? "today" : "tomorrow"} 👀`,
  pantryCtaPhoto: "PHOTO OF THE FRIDGE",
  pantryCtaManual: "TYPE IT",
  pantryShoppingLink: "Shopping list",
  pantryEmpty: "Empty as a Monday cupboard. Start with a photo or type a product.",
  pantryStapleTag: "STAPLE",
  pantryExpired: "EXPIRED",
  pantryToday: "TODAY",
  pantryTomorrow: "TOMORROW",
  pantryExpiryDays: (days) => `${days}D`,
  pantryEditAria: (name) => `edit ${name}`,
  pantryDeleteAria: (name) => `delete ${name}`,
  pantrySharedNote:
    "The fridge is shared across the household. What one of you adds, everyone sees.",

  addBack: "Fridge",
  addTopTitle: "ADD A PRODUCT",
  addEyebrow: "NEW ENTRY",
  addTitle: "Add a product",
  addIntro: "The entry goes to the shared fridge. The rest of the family sees it right away.",
  addFieldWhat: "What",
  addPlaceholderWhat: "e.g. whole milk 1L",
  addFieldQuantity: "Quantity",
  addFieldUnit: "Unit",
  addFieldCategory: "Category",
  addStapleStrong: "Staple item.",
  addStapleRest: " Always at home, the shopping list reminds you of it on its own.",
  addSubmit: "Put it in the fridge →",

  photoBack: "Fridge",
  photoTopTitle: "PHOTO OF THE FRIDGE",
  photoTitle: "Fridge? Snap it",
  photoIntro: "Open the door wide, in good light. Labels help.",
  photoNoItems:
    "I couldn't spot any products in the photo. Open the fridge wider or type it in by hand.",
  photoErrAnalyse: "Couldn't analyse it",
  photoStatusAnalysing: "● ANALYSING · SYTO SENSE",
  photoStatusRec: "● REC · SYTO SENSE",
  photoViewfinderHint: "PRODUCT LABELS GIVE US MORE TO WORK WITH",
  photoTipLight: "💡 Good light",
  photoTipFromAbove: "📐 From above",
  photoTipWholeFront: "🥑 Whole front",
  photoPreviewAlt: "fridge preview",
  photoAriaManual: "Type it by hand",
  photoAriaRetake: "Another photo",
  photoAriaShoot: "Take a photo",
  photoAriaGallery: "From the gallery",
  photoCaptionAnalysing: "PEEKING INSIDE THE FRIDGE...",
  photoCaptionRetake: "TAP THE CAMERA TO RETAKE",
  photoCaptionShoot: "TAP THE CAMERA TO SNAP",

  confirmBack: "Back",
  confirmTopTitle: "CONFIRM THE FRIDGE",
  confirmCount: (n) => (n === 1 ? "1 item" : `${n} items`),
  confirmDetected: "Detected products",
  confirmCheckHint: " · check the amounts before you save.",
  confirmEmpty: "All cleared. Go back and snap again.",
  confirmStapleTag: "STAPLE",
  confirmQtyUnknown: "AMOUNT ?",
  confirmAiFooter: "Estimated values. Check before saving.",
  confirmCta: (n) => (n === 1 ? "PUT 1 ITEM IN THE FRIDGE" : `PUT ${n} ITEMS IN THE FRIDGE`),

  editTitle: "Fix the product",
  editFieldName: "Name",
  editFieldQuantity: "Quantity",
  editFieldUnit: "Unit",
  editFieldCategory: "Category",
  editFieldExpiry: "Expiry (optional)",
  editStaple: "Staple item",

  recipesTopTitle: "SUGGESTIONS",
  recipesBack: "Fridge",
  recipesTitle: "From what you have",
  recipesCount: (n) => `${n} ${n === 1 ? "idea" : "ideas"}`,
  recipesBudgetNote: " · within your budget",
  recipesFilterAll: "All",
  recipesErrGenerate: "Couldn't generate",
  recipesLoading: "Peeking inside the fridge…",
  recipesEmpty:
    "Nothing sensible comes to mind from what you have. Add something to the fridge or change the filter.",
  recipesHasAll: "YOU HAVE IT ALL",
  recipesMissing: (n) => `MISSING ${n}`,
  recipesHaveLabel: "HAVE",
  recipesMissingLabel: "MISSING",
  recipesAddedToList: "✓ on the list",
  recipesAddingToList: "adding…",
  recipesAddToList: "+ to the list",
  recipesHowTo: "How to make it",
  recipesLogIt: "I logged this",
  recipesEdit: "Edit",
  recipesOther: "Other ideas",

  shoppingBack: "Fridge",
  shoppingTopTitle: "SHOPPING LIST",
  shoppingTitle: "Shopping",
  shoppingClear: "Toss out ✓",
  shoppingErrClear: "Couldn't clear the list",
  shoppingCountToBuy: (n) => (n === 1 ? "1 to buy" : `${n} to buy`),
  shoppingDoneNote: (n) => ` · ${n} checked off`,
  shoppingEmpty: "The list is empty. Add an item or pull one from recipe suggestions.",
  shoppingSectionToBuy: "TO BUY",
  shoppingSectionDone: "CHECKED OFF",
  shoppingSourceManual: "by hand",
  shoppingSourceRecipe: "from a recipe",
  shoppingSourcePantryEmpty: "restock",
  shoppingCheckAria: (name) => `check off ${name}`,
  shoppingUncheckAria: (name) => `uncheck ${name}`,
  shoppingDeleteAria: (name) => `delete ${name}`,
  shoppingCta: "ADD AN ITEM",

  shopAddBack: "List",
  shopAddTopTitle: "ADD TO THE LIST",
  shopAddEyebrow: "NEW ENTRY",
  shopAddTitle: "Add to the list",
  shopAddIntro: "What would come in handy? Amount and category are optional, the name is enough.",
  shopAddFieldWhat: "What to buy",
  shopAddPlaceholderWhat: "e.g. butter, bread, apples",
  shopAddFieldQuantity: "How much (optional)",
  shopAddFieldUnit: "Unit",
  shopAddFieldCategory: "Category (optional)",
  shopAddSubmit: "Add to the list →",

  cravingBack: "Journal",
  cravingTopTitle: "AN IDEA",
  cravingTitle: "I feel like",
  cravingPlaceholder: 'e.g. "something filling, low on carbs"',
  cravingBudgetPrefix: "BUDGET: ",
  cravingBudgetEmpty: "BUDGET: —",
  cravingPantryToggle: "Only from what's in my fridge",
  cravingSubmit: "Come up with a recipe",
  cravingThinking: "Thinking…",
  cravingErrGenerate: "Couldn't come up with anything",
  cravingEmpty:
    'Nothing sensible comes to mind. Try a different craving or turn off "only from the fridge".',
  cravingBestMatch: "BEST MATCH",
  cravingAlternative: "ALTERNATIVE",
  cravingStatKcal: "kcal",
  cravingStatProtein: "protein",
  cravingStatFat: "fat",
  cravingStatCarbs: "carbs",
  cravingConfHigh: "solid",
  cravingConfMid: "check it",
  cravingConfLow: "unsure",
  cravingHaveLabel: "HAVE",
  cravingMissingLabel: "MISSING",
  cravingAddedToList: "✓ on the list",
  cravingAddingToList: "adding…",
  cravingAddToList: "+ to the list",
  cravingStepByStep: "Step by step",
  cravingLogIt: "Log it",
  cravingEdit: "Edit",

  menuBack: "Journal",
  menuTopTitle: "MENU",
  menuOtherPhoto: "Another photo",
  menuPickTitle: "What are we ordering",
  menuPickIntro:
    "Snap the menu at the restaurant — you can add up to 3 photos if the menu runs several pages. We'll tell you the calories of each dish and what fits your day best.",
  menuPickPlaceholder: "Pick menu photos (up to 3)",
  menuPreviewAlt: (n) => `menu preview ${n}`,
  menuReading: "Reading the menu…",
  menuPickCta: "Take / pick photos",
  menuErrNoMenu:
    "We couldn't find a menu in the photo. Take a closer shot or a simpler menu with legible names.",
  menuResultTitle: "Top picks for you",
  menuDishCount: (n) => (n === 1 ? "1 dish" : `${n} dishes`),
  menuRemainingPrefix: " · ",
  menuRemaining: (kcal) => `${kcal} kcal left`,
  menuFilterTop3: "★ My top 3",
  menuFilterAll: "All",
  menuShowOriginal: (language) => `Show ${language}`,
  menuShowTranslated: "Show EN",
  menuTopBadge: (n) => `TOP ${n}`,
  menuCollapse: "COLLAPSE ↑",
  menuExpand: "I'LL ORDER →",
  menuLogIt: "I ordered this",
  menuEdit: "Edit",

  mealType: {
    breakfast: "breakfast",
    lunch: "lunch",
    dinner: "dinner",
    snack: "snack",
  },

  categoryLabel: {
    nabiał: "dairy",
    mięso: "meat",
    warzywa: "vegetables",
    owoce: "fruit",
    pieczywo: "bread",
    suche: "dry goods",
    napoje: "drinks",
    mrożone: "frozen",
    słodycze: "sweets",
    przyprawy: "spices",
    inne: "other",
  },

  unitLabel: {
    g: "g",
    kg: "kg",
    ml: "ml",
    l: "l",
    szt: "pcs",
    opak: "pack",
    łyż: "tbsp",
    szkl: "cup",
  },
};

const de: PantryCopy = {
  loading: "Lädt...",
  cancel: "Abbrechen",
  save: "Speichern",
  saving: "Speichert...",
  remove: "Entfernen",
  errLoad: "Konnte nicht geladen werden",
  errDelete: "Konnte nicht gelöscht werden",
  errSave: "Etwas ist schiefgelaufen",
  errUpdate: "Konnte nicht aktualisiert werden",
  errAddToList: "Konnte nicht zur Liste hinzugefügt werden",
  errLog: "Konnte nicht erfasst werden",

  pantryMeta: "KÜHLSCHRANK",
  pantrySuggestions: "Vorschläge →",
  pantryTitle: "Kühlschrank",
  pantryCountProducts: (n) => (n === 1 ? "1 Produkt" : `${n} Produkte`),
  pantryExpiringNote: (name, today) =>
    `${name} verdirbt ${today ? "heute" : "morgen"} 👀`,
  pantryCtaPhoto: "FOTO VOM KÜHLSCHRANK",
  pantryCtaManual: "EINTIPPEN",
  pantryShoppingLink: "Einkaufsliste",
  pantryEmpty: "Leer wie ein Kühlschrank am Montag. Fang mit einem Foto an oder tippe ein Produkt ein.",
  pantryStapleTag: "GRUNDVORRAT",
  pantryExpired: "ABGELAUFEN",
  pantryToday: "HEUTE",
  pantryTomorrow: "MORGEN",
  pantryExpiryDays: (days) => `${days}T`,
  pantryEditAria: (name) => `${name} bearbeiten`,
  pantryDeleteAria: (name) => `${name} löschen`,
  pantrySharedNote:
    "Der Kühlschrank ist im Haushalt geteilt. Was eine Person einträgt, sehen alle.",

  addBack: "Kühlschrank",
  addTopTitle: "PRODUKT HINZUFÜGEN",
  addEyebrow: "NEUER EINTRAG",
  addTitle: "Produkt hinzufügen",
  addIntro: "Der Eintrag landet im gemeinsamen Kühlschrank. Die Familie sieht ihn sofort.",
  addFieldWhat: "Was",
  addPlaceholderWhat: "z. B. Vollmilch 1L",
  addFieldQuantity: "Menge",
  addFieldUnit: "Einheit",
  addFieldCategory: "Kategorie",
  addStapleStrong: "Grundvorrat.",
  addStapleRest: " Immer im Haus, die Einkaufsliste erinnert von selbst daran.",
  addSubmit: "In den Kühlschrank →",

  photoBack: "Kühlschrank",
  photoTopTitle: "FOTO VOM KÜHLSCHRANK",
  photoTitle: "Kühlschrank? Klick",
  photoIntro: "Tür weit öffnen, bei gutem Licht. Etiketten helfen.",
  photoNoItems:
    "Ich habe keine Produkte auf dem Foto gefunden. Öffne den Kühlschrank weiter oder tippe von Hand ein.",
  photoErrAnalyse: "Konnte nicht analysiert werden",
  photoStatusAnalysing: "● ANALYSE · SYTO SENSE",
  photoStatusRec: "● REC · SYTO SENSE",
  photoViewfinderHint: "PRODUKTETIKETTEN GEBEN UNS MEHR ZUM ARBEITEN",
  photoTipLight: "💡 Gutes Licht",
  photoTipFromAbove: "📐 Von oben",
  photoTipWholeFront: "🥑 Ganze Front",
  photoPreviewAlt: "Kühlschrank-Vorschau",
  photoAriaManual: "Von Hand eintippen",
  photoAriaRetake: "Anderes Foto",
  photoAriaShoot: "Foto aufnehmen",
  photoAriaGallery: "Aus der Galerie",
  photoCaptionAnalysing: "WIR SCHAUEN IN DEN KÜHLSCHRANK...",
  photoCaptionRetake: "TIPPE AUF DIE KAMERA, UM ZU WIEDERHOLEN",
  photoCaptionShoot: "TIPPE AUF DIE KAMERA, UM ZU KNIPSEN",

  confirmBack: "Zurück",
  confirmTopTitle: "KÜHLSCHRANK BESTÄTIGEN",
  confirmCount: (n) => (n === 1 ? "1 Sache" : `${n} Sachen`),
  confirmDetected: "Erkannte Produkte",
  confirmCheckHint: " · prüfe die Mengen, bevor du speicherst.",
  confirmEmpty: "Alles entfernt. Geh zurück und knipse noch einmal.",
  confirmStapleTag: "GRUNDVORRAT",
  confirmQtyUnknown: "MENGE ?",
  confirmAiFooter: "Geschätzte Werte. Vor dem Speichern prüfen.",
  confirmCta: (n) =>
    n === 1 ? "1 SACHE IN DEN KÜHLSCHRANK" : `${n} SACHEN IN DEN KÜHLSCHRANK`,

  editTitle: "Produkt korrigieren",
  editFieldName: "Name",
  editFieldQuantity: "Menge",
  editFieldUnit: "Einheit",
  editFieldCategory: "Kategorie",
  editFieldExpiry: "Datum (optional)",
  editStaple: "Grundvorrat",

  recipesTopTitle: "VORSCHLÄGE",
  recipesBack: "Kühlschrank",
  recipesTitle: "Aus dem, was du hast",
  recipesCount: (n) => `${n} ${n === 1 ? "Vorschlag" : "Vorschläge"}`,
  recipesBudgetNote: " · für dein Budget",
  recipesFilterAll: "Alles",
  recipesErrGenerate: "Konnte nicht generiert werden",
  recipesLoading: "Wir schauen in den Kühlschrank…",
  recipesEmpty:
    "Aus dem, was du hast, fällt mir nichts Sinnvolles ein. Füge etwas zum Kühlschrank hinzu oder ändere den Filter.",
  recipesHasAll: "DU HAST ALLES",
  recipesMissing: (n) => `FEHLT ${n}`,
  recipesHaveLabel: "DA",
  recipesMissingLabel: "FEHLT",
  recipesAddedToList: "✓ auf der Liste",
  recipesAddingToList: "wird hinzugefügt…",
  recipesAddToList: "+ zur Liste",
  recipesHowTo: "Zubereitung",
  recipesLogIt: "Habe ich erfasst",
  recipesEdit: "Bearbeiten",
  recipesOther: "Andere Vorschläge",

  shoppingBack: "Kühlschrank",
  shoppingTopTitle: "EINKAUFSLISTE",
  shoppingTitle: "Einkauf",
  shoppingClear: "Aussortieren ✓",
  shoppingErrClear: "Liste konnte nicht geleert werden",
  shoppingCountToBuy: (n) => (n === 1 ? "1 zu kaufen" : `${n} zu kaufen`),
  shoppingDoneNote: (n) => ` · ${n} abgehakt`,
  shoppingEmpty: "Die Liste ist leer. Füge eine Sache hinzu oder hol eine aus den Rezeptvorschlägen.",
  shoppingSectionToBuy: "ZU KAUFEN",
  shoppingSectionDone: "ABGEHAKT",
  shoppingSourceManual: "manuell",
  shoppingSourceRecipe: "aus Rezept",
  shoppingSourcePantryEmpty: "Nachschub",
  shoppingCheckAria: (name) => `${name} abhaken`,
  shoppingUncheckAria: (name) => `${name} abwählen`,
  shoppingDeleteAria: (name) => `${name} löschen`,
  shoppingCta: "SACHE HINZUFÜGEN",

  shopAddBack: "Liste",
  shopAddTopTitle: "ZUR LISTE HINZUFÜGEN",
  shopAddEyebrow: "NEUER EINTRAG",
  shopAddTitle: "Zur Liste hinzufügen",
  shopAddIntro: "Was wäre nützlich? Menge und Kategorie sind optional, der Name reicht.",
  shopAddFieldWhat: "Was kaufen",
  shopAddPlaceholderWhat: "z. B. Butter, Brot, Äpfel",
  shopAddFieldQuantity: "Wie viel (optional)",
  shopAddFieldUnit: "Einheit",
  shopAddFieldCategory: "Kategorie (optional)",
  shopAddSubmit: "Zur Liste hinzufügen →",

  cravingBack: "Tagebuch",
  cravingTopTitle: "EINE IDEE",
  cravingTitle: "Ich habe Lust auf",
  cravingPlaceholder: 'z. B. „etwas Sättigendes, wenig Kohlenhydrate"',
  cravingBudgetPrefix: "BUDGET: ",
  cravingBudgetEmpty: "BUDGET: —",
  cravingPantryToggle: "Nur aus dem, was im Kühlschrank ist",
  cravingSubmit: "Rezept ausdenken",
  cravingThinking: "Ich denke nach…",
  cravingErrGenerate: "Konnte mir nichts ausdenken",
  cravingEmpty:
    'Mir fällt nichts Sinnvolles ein. Versuch eine andere Lust oder schalte „nur aus dem Kühlschrank" aus.',
  cravingBestMatch: "BESTE ÜBEREINSTIMMUNG",
  cravingAlternative: "ALTERNATIVE",
  cravingStatKcal: "kcal",
  cravingStatProtein: "Eiweiß",
  cravingStatFat: "Fett",
  cravingStatCarbs: "Kohlenhydrate",
  cravingConfHigh: "sicher",
  cravingConfMid: "prüfen",
  cravingConfLow: "unsicher",
  cravingHaveLabel: "DA",
  cravingMissingLabel: "FEHLT",
  cravingAddedToList: "✓ auf der Liste",
  cravingAddingToList: "wird hinzugefügt…",
  cravingAddToList: "+ zur Liste",
  cravingStepByStep: "Schritt für Schritt",
  cravingLogIt: "Erfassen",
  cravingEdit: "Bearbeiten",

  menuBack: "Tagebuch",
  menuTopTitle: "SPEISEKARTE",
  menuOtherPhoto: "Anderes Foto",
  menuPickTitle: "Was bestellen wir",
  menuPickIntro:
    "Knipse die Speisekarte im Restaurant — du kannst bis zu 3 Fotos hinzufügen, wenn die Karte mehrere Seiten hat. Wir sagen dir, wie viele Kalorien jedes Gericht hat und was am besten zu deinem Tag passt.",
  menuPickPlaceholder: "Speisekarten-Fotos wählen (bis zu 3)",
  menuPreviewAlt: (n) => `Speisekarten-Vorschau ${n}`,
  menuReading: "Wir lesen die Speisekarte…",
  menuPickCta: "Fotos aufnehmen / wählen",
  menuErrNoMenu:
    "Wir konnten keine Speisekarte auf dem Foto finden. Mach ein näheres Foto oder eine einfachere Karte mit lesbaren Namen.",
  menuResultTitle: "Top für dich",
  menuDishCount: (n) => (n === 1 ? "1 Gericht" : `${n} Gerichte`),
  menuRemainingPrefix: " · ",
  menuRemaining: (kcal) => `${kcal} kcal übrig`,
  menuFilterTop3: "★ Meine Top 3",
  menuFilterAll: "Alle",
  menuShowOriginal: (language) => `${language} anzeigen`,
  menuShowTranslated: "DE anzeigen",
  menuTopBadge: (n) => `TOP ${n}`,
  menuCollapse: "EINKLAPPEN ↑",
  menuExpand: "BESTELLE ICH →",
  menuLogIt: "Habe ich bestellt",
  menuEdit: "Bearbeiten",

  mealType: {
    breakfast: "Frühstück",
    lunch: "Mittagessen",
    dinner: "Abendessen",
    snack: "Snack",
  },

  categoryLabel: {
    nabiał: "Milchprodukte",
    mięso: "Fleisch",
    warzywa: "Gemüse",
    owoce: "Obst",
    pieczywo: "Backwaren",
    suche: "Trockenwaren",
    napoje: "Getränke",
    mrożone: "Tiefkühl",
    słodycze: "Süßigkeiten",
    przyprawy: "Gewürze",
    inne: "Sonstiges",
  },

  unitLabel: {
    g: "g",
    kg: "kg",
    ml: "ml",
    l: "l",
    szt: "Stk",
    opak: "Pck",
    łyż: "EL",
    szkl: "Tasse",
  },
};

export const pantryCopy: Record<Lang, PantryCopy> = { pl, en, de };
